import React from "react";
import Seo from "@/components/vlyt/Seo";
import { Apple, Smartphone, Download, PlayCircle, Zap } from "lucide-react";
import WaitlistModal from "@/components/vlyt/WaitlistModal";
import { VLYT } from "@/constants/testIds/vlyt";
import { logInstallEvent } from "@/lib/api";

const DRIVER_IMG = "https://images.pexels.com/photos/32078145/pexels-photo-32078145.jpeg";

export default function AppInstall() {
  const [open, setOpen] = React.useState(false);
  const [source, setSource] = React.useState("app_install");

  const trigger = (kind) => {
    setSource(`app_install_${kind}`);
    logInstallEvent(kind);
    setOpen(true);
  };

  return (
    <>
      <Seo title="Get Vlyt · Download & Install" canonical="https://govlyt.com/app" />
      <section className="pt-[120px] pb-16 mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="text-eyebrow mb-4">Cohort Rollout</div>
        <h1 className="text-display font-display max-w-4xl">
          Reserve your Vlyt. <span className="text-lime">We open cohort-by-cohort.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-[color:var(--vlyt-muted)] text-[17px]">
          Access opens campus-by-campus, cohort-by-cohort. Choose your install path, we&rsquo;ll email you
          when Vlyt lands on your device.
        </p>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 md:px-10 grid lg:grid-cols-3 gap-5">
        {[
          {
            icon: Apple,
            title: "iPhone",
            sub: "iOS 15.0 and above",
            testId: VLYT.app.installIos,
            kind: "ios",
            hint: "TestFlight invites go out weekly.",
          },
          {
            icon: PlayCircle,
            title: "Android",
            sub: "Android 8.0 and above",
            testId: VLYT.app.installAndroid,
            kind: "android",
            hint: "Google Play early-access preview.",
          },
          {
            icon: Download,
            title: "Install as PWA",
            sub: "Works on all modern browsers",
            testId: VLYT.app.installPwa,
            kind: "pwa",
            hint: "Add to home screen, no app store needed.",
          },
        ].map((c) => {
          const Icon = c.icon;
          return (
            <button
              type="button"
              key={c.title}
              onClick={() => trigger(c.kind)}
              data-testid={c.testId}
              className="text-left card-hover rounded-[20px] border border-[color:var(--vlyt-border)] bg-[color:var(--vlyt-surface-1)] p-8"
            >
              <div className="h-12 w-12 rounded-xl bg-[rgba(191,255,0,0.08)] ring-1 ring-lime flex items-center justify-center mb-5">
                <Icon size={20} className="text-lime" />
              </div>
              <div className="font-display text-[20px]">{c.title}</div>
              <div className="mt-1 text-[color:var(--vlyt-muted)] text-[13px] font-mono">{c.sub}</div>
              <p className="mt-6 text-[14px] text-[color:var(--vlyt-text)]">{c.hint}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-lime font-mono uppercase tracking-widest text-[12px]">
                <Zap size={12} /> Reserve access →
              </div>
            </button>
          );
        })}
      </section>

      <section className="mx-auto max-w-[1440px] px-5 md:px-10 mt-24 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="text-eyebrow mb-4">Are you a driver?</div>
          <h2 className="text-h1 font-display">
            Keep <span className="text-lime">100%</span> of every fare.
          </h2>
          <p className="mt-6 text-[color:var(--vlyt-muted)] max-w-lg">
            Vlyt earns from Royal passes, not from your trips. Verified drivers get first access to
            Royal-tier riders and daily settlement to your bank account.
          </p>
          <button
            type="button"
            onClick={() => {
              setSource("app_install_driver");
              setOpen(true);
            }}
            className="btn-lime mt-8"
            data-testid="app-driver-join-cta"
          >
            <Smartphone size={14} /> Apply to drive
          </button>
        </div>
        <div className="rounded-[24px] overflow-hidden border border-[color:var(--vlyt-border)]">
          <img src={DRIVER_IMG} alt="Vlyt driver in car" className="w-full aspect-[4/3] object-cover" loading="lazy" />
        </div>
      </section>

      <WaitlistModal open={open} onOpenChange={setOpen} source={source} />
    </>
  );
}
